import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";
import logo from "@/assets/logo.png";
import eventPhoto from "@/assets/event-photo-foosball.jpg";
import { ActivitiesAccordion } from "@/components/ActivitiesAccordion";

const LINKEDIN_URL = "https://www.linkedin.com/in/alex-danzinger";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.6, ease: "easeOut" as const },
  }),
};

export default function App() {
  return (
    <div className="min-h-screen bg-[#FAF9F5] font-body text-[#1A1D20]">
      <header
        data-testid="header-section"
        className="border-b border-slate-200/70"
      >
        <div className="mx-auto max-w-5xl px-6 py-6 sm:px-8">
          <motion.img
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            data-testid="header-logo"
            src={logo}
            alt="Logo for Foosball sales strategy and execution."
            className="h-12 w-auto sm:h-14"
          />
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 sm:px-8">
        <motion.section
          data-testid="intro-section"
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="py-12 sm:py-16"
        >
          <h1
            data-testid="intro-heading"
            className="font-heading text-4xl font-bold tracking-tight text-[#0F0750] sm:text-5xl"
          >
            What Is Foosball?
          </h1>
          <div className="mt-8 grid items-center gap-10 md:grid-cols-2">
            <div>
              <p
                data-testid="intro-text-1"
                className="text-base leading-relaxed text-slate-600 sm:text-lg"
              >
                Foosball is a Calgary-based technology, consulting, and
                investment business.
              </p>
              <p
                data-testid="intro-text-2"
                className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg"
              >
                We help ambitious companies identify important problems, build
                practical solutions, and create momentum through better use of
                software, AI, strategy, and commercial execution. Our work
                spans go-to-market consulting, software products, community
                events, and early-stage technology investments.
              </p>
              <p
                data-testid="intro-text-3"
                className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg"
              >
                The name reflects how we work. Foosball is fast-moving,
                strategic, competitive, and fun—requiring individual skill,
                teamwork, adaptability, and a little comfort with organized
                chaos. Those are the same qualities that matter when building
                companies, winning complex deals, and bringing new technology
                into the market.
              </p>
            </div>
            <img
              data-testid="intro-event-photo"
              src={eventPhoto}
              alt="Alex Danzinger with colleagues at a Foosball technology community event"
              className="w-full rounded-xl border border-slate-200 object-cover shadow-md"
            />
          </div>
        </motion.section>

        <motion.section
          data-testid="activities-section"
          custom={1}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="pb-12 sm:pb-16"
        >
          <h2
            data-testid="activities-heading"
            className="font-heading text-4xl font-bold tracking-tight text-[#0F0750] sm:text-5xl"
          >
            What We Do
          </h2>
          <p className="mt-3 text-sm text-slate-500 sm:text-base">
            Foosball focuses on four areas:
          </p>
          <div className="mt-6">
            <ActivitiesAccordion />
          </div>
        </motion.section>

        <motion.section
          data-testid="founder-section"
          custom={2}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="border-t border-slate-200/70 py-12 sm:py-16"
        >
          <h2
            data-testid="founder-heading"
            className="font-heading text-base font-semibold tracking-tight text-[#0F0750] md:text-lg"
          >
            About the Founder
          </h2>
          <p
            data-testid="founder-text"
            className="mt-6 max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            Have been an award winning &amp; sales record setting sales
            executive leading deals and deal teams that have brought in revenue
            of well over $100-M at my previous employers before diving into the
            consulting world. I've been fortunate to learn about sales,
            leadership, process, technology and delivery from my past employers
            including ADP, SAP, Workday, Celonis and Palantir.
          </p>
        </motion.section>

        <motion.section
          data-testid="connect-section"
          custom={3}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="border-t border-slate-200/70 py-12 sm:py-16"
        >
          <h2
            data-testid="connect-heading"
            className="font-heading text-base font-semibold tracking-tight text-[#0F0750] md:text-lg"
          >
            Connect With Me via LinkedIn
          </h2>
          <a
            data-testid="linkedin-button"
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2.5 rounded-full bg-[#0F0750] px-7 py-3.5 font-heading text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:scale-[1.02] hover:bg-[#241578] hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0F0750] active:scale-[0.98]"
          >
            <Linkedin className="h-4 w-4" />
            Connect With Me via LinkedIn
          </a>
        </motion.section>
      </main>

      <footer
        data-testid="footer-section"
        className="border-t border-slate-200/70"
      >
        <div className="mx-auto max-w-5xl px-6 py-8 sm:px-8">
          <p
            data-testid="footer-copyright"
            className="text-sm text-slate-500"
          >
            Copyright © 2026 Foosball Consulting, Inc. - All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
